import clsx from 'clsx'
import { Activity, Ban, ChevronDown, Clock, Coins, Layers, Target } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { chatStepCaption } from '@/components/trace/step-caption.ts'
import { Badge } from '@/components/ui/primitives.tsx'
import type { RunStreamState, TraceNode } from '@/hooks/use-run-stream.ts'
import { useI18n } from '@/i18n/index.ts'
import { PHASE, phaseLabel, rankWidth, type TapeSegment, toSegments } from './provenance-phases.ts'

/**
 * How an answer was made, shown next to the answer itself.
 *
 * The trace panel is the operator's instrument: every step, every rank, every token. This is
 * the reader's version of the same run, and the difference is deliberate — it answers "how
 * long did what take, what did it cost, and what did it actually read", which is what someone
 * deciding whether to trust a paragraph needs, in words that are outcomes rather than
 * mechanism (AGM-022; the conversation column may not describe how the machine is configured).
 *
 * The tape is the point. Each segment's width is its real duration, so a run that spent four
 * seconds reading documents and one second writing looks nothing like one that guessed — and
 * that difference is legible before anyone opens anything.
 *
 * Everything here is read from the run currently loaded in the stream state: the live run, or
 * the past run whose trace is being replayed. There is no second fetch and no second
 * read-model, which is what keeps this panel and the trace lane from ever disagreeing.
 *
 * Every word comes from `t.chat`, read inside each component body. A module-scope capture —
 * `const t = messages.chat` beside the imports — would freeze the locale at import and then
 * render the previous language after a switch, with no type error, no runtime error and no
 * failing test (PRD-27 F-3, LOC-012). The phase structs beside it carry no prose at all,
 * which is what lets THEM stay module-level.
 */

type RetrievalResult = NonNullable<TraceNode['retrieval']>['results'][number]

/**
 * A relevance score is four decimals wherever it appears, through `Intl` rather than
 * `toFixed` so the separator is the reader's (PRD-27 F-2).
 */
const SCORE_DIGITS: Intl.NumberFormatOptions = { minimumFractionDigits: 4, maximumFractionDigits: 4 }

export const Provenance = ({ state }: { state: RunStreamState }) => {
  // Bound inside the body, never at module scope (PRD-27 F-3).
  const { t: messages, formatCost, formatDuration, formatTokens } = useI18n()
  const t = messages.chat
  const [open, setOpen] = useState(false)

  const nodes = state.nodes
  if (nodes.length === 0) return null

  const outcome = state.outcome
  const live = state.status === 'running' || state.status === 'queued'
  const segments = toSegments(nodes, live && state.text.length > 0)
  const total = segments.reduce((sum, seg) => sum + (seg.ms ?? 0), 0)
  // Order matters more than uniqueness here: the legend should read left to right in the same
  // order the tape does, so a reader maps colour to phase without hunting.
  const phases = segments.filter((seg, i, all) => all.findIndex((s) => s.phase.id === seg.phase.id) === i)

  return (
    <div className="mt-5 rounded-card border border-rule bg-card px-[15px] pb-3.5 pt-3 shadow-glass">
      <div className="flex flex-wrap items-center gap-2">
        <Activity size={12} className="shrink-0 text-fg-muted" aria-hidden />
        <span className="eyebrow">{t.provenanceTitle}</span>
        {/* Decoration only. The run's live state is announced once, by the trace panel's own
            status region — a second live region on the same fact makes both unreadable. */}
        {live && <span aria-hidden className="pulse-signal h-[5px] w-[5px] rounded-full bg-primary" />}
        {outcome && (
          <span className="ml-auto flex items-center gap-3.5">
            <Total icon={Clock} label={t.totalTime} value={formatDuration(outcome.totals.durationMs)} />
            <Total
              icon={Layers}
              label={t.totalTokens}
              value={formatTokens(outcome.totals.inputTokens + outcome.totals.outputTokens)}
            />
            <Total icon={Coins} label={t.totalCost} value={formatCost(outcome.totals.costUsd)} />
          </span>
        )}
      </div>

      {/* A run that stopped short must say so where its numbers are, or a partial answer reads
          as a complete one that happened to be brief. */}
      {outcome && outcome.status !== 'completed' && (
        <div className="mt-2">
          <Badge tone={outcome.status === 'failed' ? 'alert' : 'primary'}>{t.runStatus[outcome.status]}</Badge>
        </div>
      )}

      <Tape segments={segments} total={total} live={live} />

      <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1">
        {phases.map((seg) => (
          <span key={seg.phase.id} className="readout flex items-center gap-1.5 text-[9px] text-fg-muted">
            <span aria-hidden className={clsx('h-1 w-2 shrink-0 rounded-[2px]', seg.phase.dot)} />
            {phaseLabel(t, seg.phase.id)}
          </span>
        ))}
        <span className="readout ml-auto text-[9px] text-fg-subtle">{t.tapeHint}</span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={clsx(
            'inline-flex h-[29px] items-center gap-1.5 rounded-full border border-rule-strong bg-card-solid px-3',
            'text-[12px] font-semibold tracking-[-0.012em] text-fg',
            'transition-[transform,box-shadow] duration-200 hover:-translate-y-px hover:shadow-glass'
          )}
        >
          <ChevronDown
            size={12}
            aria-hidden
            className={clsx('transition-transform duration-300 ease-out', open && 'rotate-180')}
          />
          {open ? t.hideWork : t.showWork}
        </button>
      </div>

      {/*
        Mounted only while open, rather than collapsed to zero height. Two reasons, and the
        second is not negotiable: a closed sheet should cost nothing, and `<main>` on this
        route is asserted to contain no text describing how the agent was configured — a step
        list hidden behind `max-height: 0` is still in the DOM and still counts.
      */}
      {open && (
        <div className="fade-in mt-3.5 grid gap-5 border-t border-rule pt-3.5 md:grid-cols-2">
          <Evidence nodes={nodes} state={state} />
          <Steps nodes={nodes} live={live} toolCalls={outcome?.totals.toolCalls ?? null} />
        </div>
      )}
    </div>
  )
}

const Total = ({ icon: Icon, label, value }: { icon: typeof Clock; label: string; value: string }) => (
  <span className="flex items-center gap-1.5">
    <Icon size={10} className="shrink-0 text-fg-subtle" aria-hidden />
    <span className="sr-only">{label}</span>
    <span className="readout text-[11.5px] font-medium">{value}</span>
  </span>
)

/**
 * The time-proportional tape.
 *
 * `flex: <ms> 1 0%` is the whole mechanism: the segments divide the row in exactly the ratio
 * the phases divided the run. A step still running has no duration yet, so it takes the
 * minimum width and wears the travelling sheen until it settles into its real share.
 *
 * One `role="img"` with a written summary rather than a dozen unlabelled boxes: the picture
 * says one sentence, so that sentence is what a screen reader is given.
 */
const Tape = ({ segments, total, live }: { segments: TapeSegment[]; total: number; live: boolean }) => {
  const { t: messages, formatDuration, formatList } = useI18n()
  const t = messages.chat
  // `formatList` rather than a written comma: the summary is one spoken sentence, and the two
  // languages punctuate a list differently — English takes the serial comma, Portuguese joins
  // the last two with "e" and takes none (LOC-016, D-31).
  const summary = formatList(segments.map((seg) => t.tapeSpan(phaseLabel(t, seg.phase.id), formatDuration(seg.ms))))

  return (
    <div className="mt-2.5 flex h-[30px] gap-[3px]" role="img" aria-label={t.tapeSummary(summary)}>
      {segments.map((seg) => {
        const phase = seg.phase
        const label = phaseLabel(t, phase.id)
        const Icon = phase.icon
        const ms = seg.ms ?? 0
        // A label inside a sliver is unreadable and a truncated one is worse than none.
        const labelled = total > 0 && ms / total > 0.15

        return (
          <div
            key={seg.key}
            title={t.tapeSpanTitle(label, formatDuration(seg.ms))}
            style={{ flex: `${Math.max(ms, 1)} 1 0%` }}
            className={clsx(
              'relative min-w-[8px] overflow-hidden rounded-[8px] transition-opacity duration-300',
              phase.fill,
              // Everything that already finished steps back while something is still running,
              // so the eye lands on the part of the run that is still moving.
              live && !seg.running && 'opacity-50'
            )}
          >
            {labelled && (
              <span className="absolute left-2 top-1/2 flex -translate-y-1/2 items-center gap-1.5 whitespace-nowrap">
                <Icon size={10} className={clsx(phase.ink, 'opacity-95')} aria-hidden />
                <span className={clsx('readout text-[9.5px] tracking-[0.03em] opacity-95', phase.ink)}>{label}</span>
              </span>
            )}
            {labelled && seg.ms !== null && (
              <span
                className={clsx('readout absolute right-2 top-1/2 -translate-y-1/2 text-[9px] opacity-75', phase.ink)}
              >
                {formatDuration(seg.ms)}
              </span>
            )}
            {seg.running && <span aria-hidden className="sheen absolute inset-0" />}
          </div>
        )
      })}
    </div>
  )
}

/**
 * What the run read, and how it was found.
 *
 * Both halves of the search are shown because they mean different things to a reader: a
 * passage found by its wording matched the words in the question, and one found by meaning
 * did not have to. The bar is a picture of the rank printed beside it — reciprocal rank, which
 * is the quantity the two arms are actually fused on — and never a number of its own.
 *
 * A row with no marker was retrieved and then not cited. It stays visible: "it looked at this
 * and did not use it" is part of the honest account of a run.
 */
const Evidence = ({ nodes, state }: { nodes: TraceNode[]; state: RunStreamState }) => {
  const { t: messages, formatNumber } = useI18n()
  const t = messages.chat
  const markerByChunk = new Map(state.citations.map((citation) => [citation.chunkId, citation.marker]))
  // The same passage found by two steps is one piece of evidence, not two — and deduping is
  // also what makes the chunk id a usable React key.
  const seen = new Set<string>()
  const results: RetrievalResult[] = []
  for (const node of nodes) {
    for (const result of node.retrieval?.results ?? []) {
      if (seen.has(result.chunkId)) continue
      seen.add(result.chunkId)
      results.push(result)
    }
  }
  if (results.length === 0) return null

  return (
    <div>
      <div className="mb-2.5 flex items-center gap-2">
        <Target size={11} className="shrink-0 text-fg-muted" aria-hidden />
        <span className="eyebrow">{t.evidenceTitle}</span>
        <span className="readout text-[9px] text-fg-subtle">{t.evidenceScale}</span>
      </div>

      <ul className="space-y-[7px]">
        {results.slice(0, 6).map((result) => {
          const marker = markerByChunk.get(result.chunkId)
          return (
            <li key={result.chunkId} className="rounded-tile border border-rule bg-card-solid px-2.5 py-2">
              <div className="flex items-center gap-2">
                <span
                  className={clsx(
                    'readout shrink-0 rounded-[5px] px-1.5 py-px text-[9.5px] font-medium',
                    marker === undefined ? 'bg-card text-fg-muted' : 'bg-grounded-soft text-grounded'
                  )}
                >
                  {marker ?? t.evidenceUncited}
                </span>
                <span className="min-w-0 flex-1 truncate text-[11.5px] font-semibold tracking-[-0.014em]">
                  {result.documentTitle}
                </span>
                <span className="readout shrink-0 text-[9.5px] text-fg-muted">
                  {formatNumber(result.score, SCORE_DIGITS)}
                </span>
              </div>
              <p className="readout mt-[3px] pl-[26px] text-[9.5px] text-fg-muted">{result.locator}</p>
              <RankBar lexical={result.lexicalRank} semantic={result.semanticRank} />
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const RankBar = ({ lexical, semantic }: { lexical?: number | null; semantic?: number | null }) => {
  const t = useI18n().t.chat

  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      <span className="readout w-6 shrink-0 text-right text-[9px] text-fg-subtle">{t.evidenceRank(lexical)}</span>
      <span className="flex flex-1 justify-end">
        <span aria-hidden style={{ width: rankWidth(lexical) }} className="h-1.5 rounded-l-[3px] bg-primary/75" />
      </span>
      <span aria-hidden className="h-2 w-px shrink-0 bg-rule-strong" />
      <span className="flex flex-1">
        <span aria-hidden style={{ width: rankWidth(semantic) }} className="h-1.5 rounded-r-[3px] bg-grounded/75" />
      </span>
      <span className="readout w-6 shrink-0 text-[9px] text-fg-subtle">{t.evidenceRank(semantic)}</span>
    </div>
  )
}

/**
 * The run as a timeline.
 *
 * The step's own server-written label ("Reasoning with balanced …") is deliberately NOT
 * rendered here; it names the profile the deployment chose, which is exactly what this column
 * must not put in front of a reader. It is in the trace panel, in full, where it belongs.
 */
const Steps = ({ nodes, live, toolCalls }: { nodes: TraceNode[]; live: boolean; toolCalls: number | null }) => {
  // `i18n` whole rather than destructured: `stepPassages` owns its own plural forms and
  // needs the locale to choose between them (PRD-27 D-19).
  const i18n = useI18n()
  const { formatCost, formatDuration } = i18n
  const t = i18n.t.chat

  return (
    <div>
      <div className="mb-2.5 flex items-center gap-2">
        <Layers size={11} className="shrink-0 text-fg-muted" aria-hidden />
        <span className="eyebrow">{t.stepsTitle}</span>
      </div>

      <ol>
        {nodes.map((node, index) => {
          const phase = PHASE[node.kind]
          const Icon = phase.icon
          const running = node.status === 'running'
          const last = index === nodes.length - 1
          /*
           * The chip beside a step means two different things depending on the node, which is
           * why it is not one expression.
           *
           * On a `tool_call` node it names the STEP, and a step now carries its caption as a
           * code — so it renders from the catalogue in the reader's words rather than from
           * `displayName`, which is English prose the API wrote and which was reaching this
           * column untranslated (PRD-27 D-8, LOC-033). On a `model_call` node it names the
           * tool the model REACHED FOR, which is that tool's own identity and not this step's
           * caption, so it stays as it was.
           */
          const toolChip = node.kind === 'tool_call' ? chatStepCaption(t, node) : null

          return (
            <li key={node.id} className="relative pb-3 pl-[22px] last:pb-0">
              {!last && <span aria-hidden className="absolute bottom-0 left-[7px] top-[18px] w-px bg-rule" />}
              <span
                aria-hidden
                className={clsx(
                  'absolute left-0 top-[3px] flex h-[15px] w-[15px] items-center justify-center rounded-full border',
                  running && ['pulse-signal border-transparent', phase.dot, phase.ink],
                  !running && 'border-rule-strong bg-card-solid text-fg-muted'
                )}
              >
                <Icon size={8} strokeWidth={2.8} />
              </span>

              <div className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 text-[11.5px] font-semibold tracking-[-0.014em]">
                  {phaseLabel(t, phase.id)}
                </span>
                <span className="readout shrink-0 text-[9.5px] text-fg-muted">{formatDuration(node.durationMs)}</span>
              </div>

              {/* Guarded so a step with nothing to say does not leave a row of margin behind. */}
              {(node.retrieval || node.tool || node.usage || node.status === 'failed') && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {node.retrieval && <StepChip>{t.stepPassages(i18n, node.retrieval.selected)}</StepChip>}
                  {node.tool && <StepChip>{toolChip ?? node.tool.displayName}</StepChip>}
                  {node.usage && <StepChip>{formatCost(node.usage.costUsd)}</StepChip>}
                  {node.status === 'failed' && <Badge tone="alert">{t.stepFailed}</Badge>}
                </div>
              )}
            </li>
          )
        })}
      </ol>

      {/* Only claimed when it is true, and only once the run is over: a run still in flight has
        not finished deciding whether it needs to do anything. */}
      {!live && toolCalls === 0 && (
        <p className="mt-1 flex items-center gap-1.5 pl-[22px] text-[11px] leading-relaxed text-fg-muted">
          <Ban size={11} className="shrink-0 text-grounded" aria-hidden />
          {t.noActions}
        </p>
      )}
    </div>
  )
}

const StepChip = ({ children }: { children: ReactNode }) => (
  <span className="readout rounded-full border border-rule-strong bg-card-solid px-1.5 py-px text-[9px] text-fg-muted">
    {children}
  </span>
)
